import NavBar, { NavBarRoute } from "#components/nav-bars-shared/nav-bar";
import { NavBar as NavBarV2 } from "#components/nav-bars-shared/nav-bar-v2";
import rootStore from "#stores/index";
import { useSandwichMenu } from "#structure/providers/sandwich-menu";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { useTranslation } from "react-i18next";
import {
	NavBarRouteV2,
	RouteItemProps,
	RouteItemPropsV2,
	useNavigationWithRestriction,
} from "../navigation-with-permissions";
import { useActiveEvents } from "../use-active-events";
import { useModularization } from "../use-modularization";
import { useGetCurrentEmployee } from "#resources/hooks/integrations/auth/query";
import { useVendorRestrictionGuard } from "../use-vendor-restriction-guard";
import { usePermission } from "#resources/hooks";
import { EnterprisePermissions } from "#resources/helpers/permissions/utils";

const CATERING_PERMISSIONS: EnterprisePermissions[] = [
	"createCateringProposals",
	"viewCateringReports",
	"configureCatering",
];

function Nav() {
	const { organizationStore } = React.useContext(rootStore);
	const { t } = useTranslation("nav", { keyPrefix: "navbar.org" });
	const {
		isOpenSandwichMenu,
		onChangeIsOpenSandwichMenu,
		currentSelectedMenu,
	} = useSandwichMenu();

	const { data: currentEmployee } = useGetCurrentEmployee({});
	const hasVendorRestrictions = !!currentEmployee?.vendors?.length;

	const { activeEvents } = useActiveEvents();

	const hasLoyaltyOrgFeature = organizationStore.organization?.features.some(
		feature => feature.id === "loyalty",
	);
	const hasGiftbackOrgFeature = organizationStore.organization?.features.some(
		feature => feature.id === "giftbackFeature",
	);

	const { showMenuV2 } = useModularization();

	const { hasPermission } = usePermission();
	const grantedCateringPermission = CATERING_PERMISSIONS.find(hasPermission);
	const cateringEntryPermissions = grantedCateringPermission
		? [grantedCateringPermission]
		: CATERING_PERMISSIONS;

	const routesV1: RouteItemProps[] = [
		{
			icon: "panel",
			label: t($ => $.panel),
			url: "/",
			allowWhenVendorRestricted: true,
		},
		{
			icon: "employee",
			label: t($ => $.team),
			url: "/employees",
		},
		{
			icon: "stock-inventory",
			label: t($ => $.stockInventory),
			url: `/products`,
			permissions: ["seeOrganizationProducts"],
		},
		{
			icon: "tax-data",
			label: t($ => $.taxData),
			permissions: ["seeOrganizationFiscalData"],
			url: "/fiscal-data",
		},
		{
			icon: "financial-report",
			label: t($ => $.financialReport),
			url: "/reports",
			permissions: ["seeOrganizationReportsPage"],
		},
		{
			icon: "financial-report",
			label: t($ => $.messagesAnalytics),
			url: "/messages-analytics",
			permissions: ["seeOrganizationReportsPage"],
		},
		{
			icon: "chart-of-accounts",
			label: t($ => $.chartOfAccounts),
			url: "/chart-of-accounts",
			permissions: ["accessFinancialBackoffice"],
		},
		{
			icon: "satisfaction-survey",
			label: t($ => $.satisfactionSurvey),
			url: "/satisfaction-survey",
			permissions: ["manageNpsSurvey"],
		},
		{
			tagType: hasLoyaltyOrgFeature ? undefined : "comingSoon",
			icon: "loyalty",
			label: t($ => $.loyalty),
			url: hasLoyaltyOrgFeature ? "/loyalty" : "/loyalty/comming-soon",
			orgFeaturesIds: "loyalty",
		},
		{
			icon: "giftback",
			label: "Giftback",
			url: "/giftback",
			hide: !hasGiftbackOrgFeature,
		},
		// {
		// 	icon: "gift-card",
		// 	label: t("giftCard"),
		// 	url: "/gift-card",
		// 	orgFeaturesIds: "giftCardFeature",
		// 	permissions: ["manageGiftCards"],
		// },
		{
			icon: "customers",
			label: t($ => $.customers),
			url: "/client-area",
		},
		{
			icon: "communication",
			label: t($ => $.communication),
			url: "/communication",
			permissions: ["manageMessage"],
		},
		{
			icon: "multivendor",
			label: t($ => $.vendors),
			url: "/vendors",
			orgFeaturesIds: "multivendor",
			permissions: ["manageVendors"],
		},
		{
			icon: "megaphone",
			label: t($ => $.internalSalesCampaign),
			url: "/internal-sales-campaign",
			permissions: ["campaignManagement"],
			orgFeaturesIds: "campaignModule",
		},
		{
			icon: "financial",
			label: t($ => $.catering),
			url: "/catering",
			permissions: cateringEntryPermissions,
		},
		{
			icon: "settings",
			label: t($ => $.integrations),
			tagType: "new",
			url: "/integrations",
			permissions: ["manageIntegrationToken"],
		},
	];

	const routesV2: RouteItemPropsV2[] = [
		{
			icon: "place",
			label: t($ => $.panel),
			url: "/",
			allowWhenVendorRestricted: true,
		},
		{
			icon: "team",
			label: t($ => $.employee),
			url: "/employees",
			alternativeUrls: ["/employees-roles"],
		},
		{
			icon: "cargo-cart",
			label: t($ => $.stockInventory),
			submenus: [
				{
					label: t($ => $.productRegistration),
					url: "/products",
				},
				{
					label: t($ => $.categories),
					url: "/categories",
				},
			],
		},
		{
			icon: "dashboard-chart",
			label: t($ => $.clientArea),
			url: "/client-area",
			orgFeaturesIds: "clientAreaFeature",
		},
		{
			icon: "crm",
			label: t($ => $.crm),
			submenus: [
				{
					label: t($ => $.customersList),
					url: "/clients",
					permissions: ["seePlaceCustomersPage"],
				},
				// {
				// 	label: t("giftCard"),
				// 	url: "/gift-card",
				// 	orgFeaturesIds: "giftCardFeature",
				// 	permissions: ["manageGiftCards"],
				// },
				{
					label: t($ => $.sendingMessages),
					permissions: ["manageMessage"],
					url: "/communication",
				},
				{
					label: t($ => $.satisfactionSurvey),
					url: "/satisfaction-survey#results",
					permissions: ["manageNpsSurvey"],
					alternativeUrls: [
						"/satisfaction-survey",
						"/satisfaction-survey#share-settings",
						"/satisfaction-survey#search-settings",
						"/satisfaction-survey#sms-report",
					],
				},
				{
					tagType: hasLoyaltyOrgFeature ? undefined : "comingSoon",
					label: t($ => $.loyalty),
					url: hasLoyaltyOrgFeature ? "/loyalty" : "/loyalty/comming-soon",
					orgFeaturesIds: "loyalty",
				},
			],
		},
		{
			icon: "reports",
			label: t($ => $.reports),
			permissions: ["seeOrganizationReportsPage"],
			submenus: [
				{
					label: t($ => $.financialReport),
					url: "/reports#revenue",
					alternativeUrls: [
						"/reports",
						"/reports#service",
						"/reports#products",
						"/reports#people",
						"/reports#ticket",
						"/reports#entrance",
					],
				},
				{
					label: t($ => $.messagesAnalytics),
					url: "/messages-analytics",
				},
			],
		},
		{
			icon: "fiscal",
			label: t($ => $.taxRegistration),
			permissions: ["seeOrganizationFiscalData"],
			url: "/fiscal-data#fiscal-profile",
			alternativeUrls: [
				"/fiscal-data",
				"/fiscal-data#fiscal-product-groups",
				"/fiscal-data#fiscal-couvert",
				"/fiscal-data#fiscal-minimum-consummation",
				"/fiscal-data#fiscal-recharge",
			],
		},
		{
			icon: "chart-of-accounts-v2",
			label: t($ => $.chartOfAccounts),
			url: "/chart-of-accounts",
			permissions: ["accessFinancialBackoffice"],
		},
		{
			icon: "multivendor-v2",
			label: t($ => $.vendors),
			url: "/vendors",
			orgFeaturesIds: "multivendor",
			permissions: ["manageVendors"],
		},
		{
			icon: "giftback",
			label: t($ => $.giftback),
			url: "/giftback",
			hide: !hasGiftbackOrgFeature,
		},
		{
			icon: "megaphone",
			label: t($ => $.internalSalesCampaign),
			url: "/internal-sales-campaign",
			permissions: ["campaignManagement"],
			orgFeaturesIds: "campaignModule",
		},
		{
			icon: "financial",
			label: t($ => $.catering),
			url: "/catering",
			permissions: cateringEntryPermissions,
		},
		{
			icon: "settings",
			label: t($ => $.settings),
			tagType: "new", // TODO: remove this 2 months after implementation
			permissions: ["manageIntegrationToken"], // TODO: remove this when new itens are added to settings menu
			submenus: [
				{
					label: t($ => $.integrations),
					url: "/integrations",
					permissions: ["manageIntegrationToken"],
				},
			],
		},
	];

	const navigationContext = React.useMemo(() => ({ hasVendorRestrictions }), [
		hasVendorRestrictions,
	]);

	const items = showMenuV2
		? useNavigationWithRestriction<NavBarRouteV2>(routesV2, navigationContext)
		: useNavigationWithRestriction(routesV1, navigationContext);

	// Protege rotas contra acesso direto quando há restrições de vendor
	useVendorRestrictionGuard(showMenuV2 ? routesV2 : routesV1, navigationContext);

	if (showMenuV2) {
		return (
			<NavBarV2
				runningEvents={activeEvents}
				items={items.restrictedRoutes as NavBarRouteV2[]}
				isOpenSandwichMenu={isOpenSandwichMenu}
				onChangeIsOpenSandwichMenu={onChangeIsOpenSandwichMenu}
				selectedMenu={currentSelectedMenu}
				showRunningEventsIdx={0}
			/>
		);
	}

	return (
		<NavBar
			items={items.restrictedRoutes as NavBarRoute[]}
			isOpenSandwichMenu={isOpenSandwichMenu}
			onChangeIsOpenSandwichMenu={onChangeIsOpenSandwichMenu}
		/>
	);
}

export default observer(Nav);
